import { useAuth } from "@clerk/clerk-react";
import {
  Box,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  Link,
  Slide,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { AlertContext } from "contexts/Alert";
import {
  EmailBrandPage,
  OnboardingDialogsContext,
} from "contexts/OnboardingDialogs";
import { OrganizationUserContext } from "contexts/Organization";
import { QuickSendDrawerContext } from "contexts/QuickSendDrawer";
import {
  ENTER_DELAY,
  EXIT_DELAY,
  SlideAnimationContext,
} from "contexts/SlideAnimation";
import { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { BentoBrand } from "schemas/dashboard";
import { Map } from "schemas/functions";
import { routes } from "schemas/routes";

import EmailBrandForm from "features/Auth/Onboarding/EmailBrandForm";
import SelectBrandForm from "features/Auth/Onboarding/SelectBrandForm";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";
import { makeDeepCopy } from "utils/updateLocalState";

const styles = {
  formContainer: {
    p: { xs: 3, sm: 5, md: 6 },
  },
  closeIcon: {
    color: "primary.main",
    fontSize: 14,
    position: "absolute",
    right: 10,
    top: 10,
  },
  noOverflow: {
    overflowX: "hidden",
  },
  fullWidth: {
    width: "100%",
  },
};

const EmailBrandDialog = () => {
  const theme = useTheme();
  const isMobileScreen = useMediaQuery(theme.breakpoints.down("md"));
  const { setAlert } = useContext(AlertContext);
  const { getToken } = useAuth();
  const { currentUser, profile, currentOrg } = useContext(
    OrganizationUserContext,
  );
  const { handleOpenQuickSendIndividual, closeDrawer } = useContext(
    QuickSendDrawerContext,
  );
  const {
    setEmailBrandDialogOpen,
    emailBrandDialogPage,
    setEmailBrandDialogPage,
  } = useContext(OnboardingDialogsContext);
  const { slideOutLeft, slideInLeft, slideOutRight, slideInRight, slideProps } =
    useContext(SlideAnimationContext);
  const [loading, setLoading] = useState<boolean>(false);

  const [onboardingForm, setOnboardingForm] = useState<Map>({});
  const [selectedBrand, setSelectedBrand] = useState<BentoBrand | undefined>();
  const [recommendedBrands, setRecommendedBrands] = useState<BentoBrand[]>([]);
  const [cursor, setCursor] = useState<number[]>([]);

  const fetchBrandRecommendations = async (restart: boolean = false) => {
    setLoading(true);
    if (restart) {
      setCursor([]);
    }
    try {
      let url = `/api/organization/${currentOrg?.id}/onboarding/brand-recommendations`;
      if (cursor && !restart) {
        url += `?cursor=${JSON.stringify(cursor)}`;
      }
      const res = await fetcherAuth(getToken, url, "GET");
      setCursor(res.cursor);
      setRecommendedBrands(res.bentoBrands);
    } catch (error) {
      setAlert(
        error?.message ||
          "Unable to fetch brand recommendations. Please reload and try again",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (profile?.id) {
      const copy = makeDeepCopy(profile);
      copy.name = currentUser?.name;
      setOnboardingForm(copy);
    }

    setEmailBrandDialogPage(EmailBrandPage.selectBrand);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);

  useEffect(() => {
    slideInLeft();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const animateNext = async (currentPage: EmailBrandPage) => {
    await slideOutLeft();
    if (currentPage === EmailBrandPage.selectBrand) {
      setEmailBrandDialogPage(EmailBrandPage.emailBrand);
    } else {
      setEmailBrandDialogPage(EmailBrandPage.complete);
    }
    slideInLeft();
  };

  const handleNext = async () => {
    trackEvent("Email Brand Dialog - Next Button Pressed", {
      page: emailBrandDialogPage,
    });

    if (emailBrandDialogPage === EmailBrandPage.complete) {
      setEmailBrandDialogOpen(false);
      return;
    }
    await animateNext(emailBrandDialogPage);
  };

  const handleBack = async () => {
    trackEvent("Email Brand Dialog - Back Button Pressed", {
      page: emailBrandDialogPage,
    });

    await slideOutRight();
    if (emailBrandDialogPage === EmailBrandPage.selectBrand) {
      setEmailBrandDialogOpen(false);
    } else if (emailBrandDialogPage === EmailBrandPage.emailBrand) {
      setEmailBrandDialogPage(EmailBrandPage.selectBrand);
    }
    slideInRight();
  };

  const fetchEmailPreview = async (selectedBrandId: number) => {
    handleOpenQuickSendIndividual(selectedBrandId, "Onboarding Form");
  };

  const handleClose = () => {
    trackEvent("Email Brand Dialog Closed", {
      page: emailBrandDialogPage,
    });

    setEmailBrandDialogOpen(false);
    closeDrawer();
  };

  return (
    <Dialog
      maxWidth="lg"
      fullWidth
      fullScreen={isMobileScreen}
      scroll="body"
      open={true}
      onClose={handleClose}
    >
      <IconButton sx={styles.closeIcon} onClick={handleClose}>
        <Box component="i" className="fa-regular fa-xmark" />
      </IconButton>
      <DialogContent sx={styles.noOverflow}>
        <Grid container sx={[styles.noOverflow, styles.fullWidth]}>
          <Slide
            in={slideProps.in}
            direction={slideProps.direction}
            easing={{
              enter: theme.transitions.easing.easeInOut,
              exit: theme.transitions.easing.easeIn,
            }}
            timeout={{
              enter: ENTER_DELAY,
              exit: EXIT_DELAY,
            }}
          >
            <Grid sx={[styles.formContainer, styles.fullWidth]} item xs={12}>
              {emailBrandDialogPage === EmailBrandPage.selectBrand && (
                <SelectBrandForm
                  onboardingForm={onboardingForm}
                  updateSelectedBrand={setSelectedBrand}
                  recommendedBrands={recommendedBrands}
                  refreshBrands={fetchBrandRecommendations}
                  loading={loading}
                  handleBack={handleBack}
                  handleNext={handleNext}
                />
              )}
              {emailBrandDialogPage === EmailBrandPage.emailBrand && (
                <EmailBrandForm
                  handleBack={handleBack}
                  selectedBrand={selectedBrand}
                  loading={loading}
                  fetchEmailPreview={fetchEmailPreview}
                />
              )}
              {emailBrandDialogPage === EmailBrandPage.complete && (
                <>
                  <Typography
                    variant="h3"
                    paragraph
                    sx={{ textAlign: "center" }}
                  >
                    <Box
                      component="i"
                      color="primary.main"
                      sx={{ pr: 2 }}
                      className="fa-solid fa-party-horn"
                    />
                    Email Sent!
                  </Typography>
                  <Typography
                    variant="subtitle1"
                    paragraph
                    sx={{ textAlign: "center" }}
                  >
                    Congrats on sending your first email! You can find hundreds
                    of thousands of more brands to pitch to you on{" "}
                    <Link
                      component={RouterLink}
                      to={`/${routes.brands}`}
                      onClick={() => {
                        trackEvent("Email Brand Dialog - Opened Brands Page", {
                          page: emailBrandDialogPage,
                        });
                        setEmailBrandDialogOpen(false);
                      }}
                      sx={{ textDecoration: "none" }}
                    >
                      <strong>brands page</strong>.
                    </Link>
                  </Typography>
                </>
              )}
            </Grid>
          </Slide>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default EmailBrandDialog;
